import { ReactNode, useState } from 'react';
import {
    SupplyOrderState,
    useMarkSupplyOrderAsCheckedMutation,
    useMarkSupplyOrderAsConfirmedMutation,
    useMarkSupplyOrderAsCreatedMutation,
    useMarkSupplyOrderAsInrackMutation,
    useMarkSupplyOrderAsOnHoldMutation,
    useMarkSupplyOrderAsOrderedMutation,
    useMarkSupplyOrderAsReadyMutation,
    useMarkSupplyOrderAsReceivedMutation,
} from 'data/__generated__';

import { confirmPopup } from 'components/Popups/Confirm';
import {
    MissingOdooSupplierPopup,
    getSuppliersMissingOdoo,
    OdooOrderSupplier,
} from '../Popups/MissingOdooSupplierPopup';

import { computeHourDeliveryFromDate } from 'helpers/dateTimes';

import { handleUpdateActionEndToast } from '../shared/handleUpdateActionEndToast';
import { SUPPLY_ORDER_STATES_LABELS } from '../constants/states';

/**
 * Transitions autorisées depuis chaque état, telles que la fiche les propose.
 *
 * C'est une table, et non neuf boutons désactivés au cas par cas : la question « que puis-je faire
 * maintenant ? » se lit alors d'une ligne, et l'écran n'affiche que les transitions possibles au lieu
 * de les griser toutes. Le serveur reste seul juge — il revalide chaque passage.
 */
export const ALLOWED_TRANSITIONS: Record<SupplyOrderState, SupplyOrderState[]> = {
    [SupplyOrderState.Created]: [SupplyOrderState.Ready, SupplyOrderState.Aborted],
    [SupplyOrderState.Ready]: [SupplyOrderState.Created, SupplyOrderState.Ordered, SupplyOrderState.Aborted],
    [SupplyOrderState.Ordered]: [
        SupplyOrderState.Confirmed,
        SupplyOrderState.OnHold,
        SupplyOrderState.Received,
        SupplyOrderState.Aborted,
    ],
    [SupplyOrderState.Confirmed]: [
        SupplyOrderState.Ordered,
        SupplyOrderState.OnHold,
        SupplyOrderState.Received,
        SupplyOrderState.Aborted,
    ],
    [SupplyOrderState.OnHold]: [
        SupplyOrderState.Ordered,
        SupplyOrderState.Confirmed,
        SupplyOrderState.Received,
        SupplyOrderState.Aborted,
    ],
    [SupplyOrderState.Received]: [
        SupplyOrderState.Ordered,
        SupplyOrderState.Confirmed,
        SupplyOrderState.OnHold,
        SupplyOrderState.Checked,
    ],
    [SupplyOrderState.Checked]: [SupplyOrderState.Received, SupplyOrderState.InRack],
    [SupplyOrderState.InRack]: [],
    [SupplyOrderState.Aborted]: [],
};

/** Avertissement affiché dans la confirmation, quand le passage a un effet de bord non réversible. */
const TRANSITION_WARNINGS: Partial<Record<SupplyOrderState, string>> = {
    [SupplyOrderState.Checked]:
        'Les quantités reçues non renseignées seront considérées comme étant celles confirmées par le fournisseur (si renseignées) ou commandées.',
    [SupplyOrderState.InRack]:
        "Le stock sera mis à jour avec les quantités renseignées comme reçues, et l'achat ne sera plus rangeable sur la totops.",
};

export type SupplyOrderStateAction = {
    state: SupplyOrderState;
    label: string;
    run: () => Promise<void>;
};

export function useSupplyOrderStateActions({
    supplyOrderId,
    supplier,
    state,
    wasAlreadyOrdered,
    onAbort,
}: {
    supplyOrderId: string;
    supplier: OdooOrderSupplier;
    state: SupplyOrderState;
    wasAlreadyOrdered: boolean;
    onAbort: () => void;
}): {
    actions: SupplyOrderStateAction[];
    loading: boolean;
    markAsOrderedWithoutEmail: () => Promise<void>;
    missingOdooSupplierPopup: ReactNode;
} {
    const [isMissingOdooPopupOpen, setIsMissingOdooPopupOpen] = useState(false);
    const missingSuppliers = getSuppliersMissingOdoo([supplier]);
    const supplyOrderIds = [supplyOrderId];

    const [markAsCreated, { loading: createdLoading }] = useMarkSupplyOrderAsCreatedMutation({
        variables: { supplyOrderIds },
    });
    const [markAsReady, { loading: readyLoading }] = useMarkSupplyOrderAsReadyMutation({
        variables: { supplyOrderIds },
    });
    const [markAsOrdered, { loading: orderedLoading }] = useMarkSupplyOrderAsOrderedMutation();
    const [markAsConfirmed, { loading: confirmedLoading }] = useMarkSupplyOrderAsConfirmedMutation({
        variables: { supplyOrderIds },
    });
    const [markAsOnHold, { loading: onHoldLoading }] = useMarkSupplyOrderAsOnHoldMutation({
        variables: { supplyOrderId },
    });
    const [markAsReceived, { loading: receivedLoading }] = useMarkSupplyOrderAsReceivedMutation();
    const [markAsChecked, { loading: checkedLoading }] = useMarkSupplyOrderAsCheckedMutation({
        variables: { supplyOrderIds },
    });
    const [markAsInRack, { loading: inRackLoading }] = useMarkSupplyOrderAsInrackMutation({
        variables: { supplyOrderIds },
    });

    const loading =
        createdLoading ||
        readyLoading ||
        orderedLoading ||
        confirmedLoading ||
        onHoldLoading ||
        receivedLoading ||
        checkedLoading ||
        inRackLoading;

    async function confirmThen(targetState: SupplyOrderState, action: () => Promise<void>, extraMessage?: string) {
        if (targetState === SupplyOrderState.Ordered && missingSuppliers.length) {
            setIsMissingOdooPopupOpen(true);
            return;
        }
        const label = SUPPLY_ORDER_STATES_LABELS[targetState].toLowerCase();
        const warning = extraMessage ?? TRANSITION_WARNINGS[targetState];
        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: `Souhaitez-vous vraiment marquer l'achat comme « ${label} » ?${warning ? `\n\n${warning}` : ''}`,
        });
        if (hasConfirmed) {
            await action();
        }
    }

    function reportResult({
        updatedSupplyOrders,
        errors,
        warnings,
        targetState,
    }: {
        updatedSupplyOrders: { _id: string }[];
        errors: string[];
        warnings?: string[] | null;
        targetState: SupplyOrderState;
    }) {
        handleUpdateActionEndToast({
            displaySuccess: !!updatedSupplyOrders.length,
            successText: `L'achat a bien été marqué comme '${SUPPLY_ORDER_STATES_LABELS[targetState].toLowerCase()}'`,
            errors,
            warnings: warnings ?? undefined,
        });
    }

    async function runMarkAsOrdered(shouldSendMailToSupplier: boolean) {
        const { data } = await markAsOrdered({ variables: { supplyOrderIds, shouldSendMailToSupplier } });
        if (!data) {
            throw new Error("Une erreur est survenue lors du passage de l'achat en 'commandé'");
        }
        reportResult({ ...data.markSupplyOrdersAsOrdered, targetState: SupplyOrderState.Ordered });
    }

    const runners: Partial<Record<SupplyOrderState, () => Promise<void>>> = {
        [SupplyOrderState.Created]: () =>
            confirmThen(SupplyOrderState.Created, async () => {
                const { data } = await markAsCreated();
                if (!data) throw new Error("Une erreur est survenue lors du passage de l'achat en 'créé'");
                reportResult({ ...data.markSupplyOrdersAsCreated, targetState: SupplyOrderState.Created });
            }),
        [SupplyOrderState.Ready]: () =>
            confirmThen(SupplyOrderState.Ready, async () => {
                const { data } = await markAsReady();
                if (!data) throw new Error("Une erreur est survenue lors du passage de l'achat en 'à envoyer'");
                reportResult({ ...data.markSupplyOrdersAsReady, targetState: SupplyOrderState.Ready });
            }),
        [SupplyOrderState.Ordered]: () =>
            confirmThen(
                SupplyOrderState.Ordered,
                () => runMarkAsOrdered(true),
                wasAlreadyOrdered
                    ? "Aucun mail ne sera envoyé au fournisseur car cette commande était déjà en 'Commandé' avant."
                    : 'Attention ! Un mail sera envoyé au fournisseur avec le bon de commande généré sur le Google Drive.',
            ),
        [SupplyOrderState.Confirmed]: () =>
            confirmThen(SupplyOrderState.Confirmed, async () => {
                const { data } = await markAsConfirmed();
                if (!data) throw new Error("Une erreur est survenue lors du passage de l'achat en 'confirmé'");
                reportResult({ ...data.markSupplyOrdersAsConfirmed, targetState: SupplyOrderState.Confirmed });
            }),
        [SupplyOrderState.OnHold]: () =>
            confirmThen(SupplyOrderState.OnHold, async () => {
                const { data } = await markAsOnHold();
                if (!data) throw new Error("Une erreur est survenue lors du passage de l'achat en 'en attente'");
                reportResult({
                    updatedSupplyOrders: [data.markSupplyOrderAsOnHold],
                    errors: [],
                    targetState: SupplyOrderState.OnHold,
                });
            }),
        [SupplyOrderState.Received]: () =>
            confirmThen(SupplyOrderState.Received, async () => {
                const { data } = await markAsReceived({
                    variables: { supplyOrderIds, hourDeliveryReceivedAt: computeHourDeliveryFromDate(new Date()) },
                });
                if (!data) throw new Error("Une erreur est survenue lors du passage de l'achat en 'reçu'");
                reportResult({ ...data.markSupplyOrdersAsReceived, targetState: SupplyOrderState.Received });
            }),
        [SupplyOrderState.Checked]: () =>
            confirmThen(SupplyOrderState.Checked, async () => {
                const { data } = await markAsChecked();
                if (!data) throw new Error("Une erreur est survenue lors du passage de l'achat en 'vérifié'");
                reportResult({ ...data.markSupplyOrdersAsChecked, targetState: SupplyOrderState.Checked });
            }),
        [SupplyOrderState.InRack]: () =>
            confirmThen(SupplyOrderState.InRack, async () => {
                const { data } = await markAsInRack();
                if (!data) throw new Error("Une erreur est survenue lors du passage de l'achat en 'rangé'");
                reportResult({ ...data.markSupplyOrdersAsInRack, targetState: SupplyOrderState.InRack });
            }),
        // L'annulation demande une raison : elle passe par sa propre fenêtre.
        [SupplyOrderState.Aborted]: async () => onAbort(),
    };

    const actions = ALLOWED_TRANSITIONS[state].map((targetState) => ({
        state: targetState,
        label: SUPPLY_ORDER_STATES_LABELS[targetState],
        run: runners[targetState] ?? (async () => undefined),
    }));

    return {
        actions,
        loading,
        missingOdooSupplierPopup: (
            <MissingOdooSupplierPopup
                suppliers={missingSuppliers}
                isOpen={isMissingOdooPopupOpen}
                setIsOpen={setIsMissingOdooPopupOpen}
            />
        ),
        markAsOrderedWithoutEmail: () =>
            confirmThen(
                SupplyOrderState.Ordered,
                () => runMarkAsOrdered(false),
                'Aucun mail ne sera envoyé au fournisseur : le bon de commande devra lui être transmis à la main.',
            ),
    };
}
